import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'src/store';
import { useApplicationsApi } from '../api';
import { getDefaultApplicationSettings } from '../helpers';
import {
  resetError,
  setApplication,
  setApplicationSettings,
  setError,
  setLoading,
} from '../slices';
import { ApplicationSettings } from '../types';

export const useSelectedApplicationSettingsService = () => {
  const dispatch = useDispatch();
  const { loading, selectedApplicationSettings, error } = useAppSelector((state) => state.applications);
  const defaultApplicationSettings = getDefaultApplicationSettings();

  const api = useApplicationsApi();

  const getApplicationSettingsById = useCallback(async (applicationId: string) => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ getSettingsById: true }));

      const response = await api.getAdminApplication(applicationId);
      const settings: ApplicationSettings = {
        ...defaultApplicationSettings,
        ...JSON.parse(response.data.settings),
        applicationId,
      };

      dispatch(setApplication(response.data));
      dispatch(setApplicationSettings(settings));

      return settings;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getSettingsById: false }));
    }
  }, []);

  const getApplicationSettingsByDomain = useCallback(async () => {
    try {
      dispatch(resetError());
      dispatch(setLoading({ getSettingsByDomain: true }));

      const response = await api.getApplicationSettingsByDomain();
      const settings: ApplicationSettings = { ...defaultApplicationSettings, ...response.data };

      dispatch(setApplicationSettings(settings));

      return settings;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getSettingsByDomain: false }));
    }
  }, []);

  return {
    loading,
    selectedApplicationSettings,
    error,
    getApplicationSettingsById,
    getApplicationSettingsByDomain,
  };
};
